import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourierIntegration } from '../../models';
import { ManifestsFiltersPanel } from './ManifestsFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
  allColumns: any[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  fixedColumns?: string[];
  searchTerm: string;
  filterConditions: FilterConditionOutput[];
  courierIntegrations?: CourierIntegration[];
}

export const FiltersSection = ({
  handleSearch,
  onFilterChange,
  allColumns,
  selectedColumns,
  onColumnsChange,
  fixedColumns = [],
  searchTerm,
  filterConditions,
  courierIntegrations = [],
}: FiltersSectionProps) => {
  const { t } = useTranslation('manifestsModule');
  const [inputValue, setInputValue] = useState(searchTerm);

  const activeFiltersCount = useMemo(() => filterConditions.length, [filterConditions.length]);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      onFilterChange?.(conditions);
    },
    [onFilterChange],
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSearchSubmit = () => {
    const trimmedValue = inputValue.trim();
    handleSearch(trimmedValue);
  };

  return (
    <>
      <Search
        placeholder={t('filters.search_placeholder', 'Buscar por numero de manifiesto...')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />
      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
      >
        <ManifestsFiltersPanel
          initialConditions={filterConditions}
          onChange={handleFilterChange}
          courierIntegrations={courierIntegrations}
        />
      </FiltersDropdown>
      <ColumnSelector
        columns={allColumns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={fixedColumns}
        storageKey="manifests-list"
        dataTestId="manifests-column-selector"
      />
    </>
  );
};
